import React, { useEffect, useState } from "react";
import { useStore } from "../../../store/store";
import fractionToInt from "../../../util/fractionToInt";
import findMinMaxInswing from "../findMaxInswing";
import findMinMaxOutswing from "../findMaxOutswing";
import findMinMaxAlumSwing from "../findMinMaxAlumSwing";
import { SelectChangeEvent } from "@mui/material";
import useSwingWidth from "../../../hooks/useSwingWidth";

interface UpdateObj {
  swingPanelWidth?: string;
  swingPanelFraction?: string;
  dropseal?: string;
}

function useWidthSelector(
  direction: string,
  sideLites: string,
  swingType: string,
  isPivot: boolean,
  editBtn: boolean,
) {
  const addToQuote = useStore((state) => state.addToQuote);

  const equalPanels = useStore((state) => state.quote.equalPanels);

  const handleError = useStore((state) => state.handleError);
  const errors = useStore((state) => state.errors);

  const material = useStore((state) => state.quote.material);

  const unitSize = useStore((state) => state.quote.unitSize);
  const unitSizeFraction = useStore((state) => state.quote.unitSizeFraction);

  const overall = fractionToInt(unitSize, unitSizeFraction);

  const swingPanelWidth = useStore((state) => state.quote.swingPanelWidth);
  const swingPanelFraction = useStore(
    (state) => state.quote.swingPanelFraction,
  );

  const screenType = useStore((state) => state.quote.screenType);
  const addScreen = useStore((state) => state.quote.addScreen);

  const alumThickFrame = useStore((state) => state.quote.alumThickFrame);

  const overallPW = fractionToInt(swingPanelWidth || "", swingPanelFraction);

  const stilesAndRailsCategory = useStore((state) => state.quote.stileCategory);

  const isPico = stilesAndRailsCategory?.includes("Pico") ? true : false;

  const exactConfig = useStore((state) => state.quote.exactConfig);

  const [minWidth, setMinWidth] = useState(0);
  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    if (direction !== "Inswing") return;
    var { min, max } =
      material === "All Aluminum"
        ? findMinMaxAlumSwing(
            equalPanels || false,
            sideLites,
            swingType,
            alumThickFrame || false,
          )
        : findMinMaxInswing(
            equalPanels || false,
            sideLites,
            swingType,
            isPico,
            isPivot || false,
          );

    setMinWidth(min || 0);
    setMaxWidth(max || 0);
  }, [direction, sideLites, swingType, equalPanels, stilesAndRailsCategory]);

  useEffect(() => {
    if (direction !== "Outswing") return;
    var { min, max } =
      material === "All Aluminum"
        ? findMinMaxAlumSwing(
            equalPanels || false,
            sideLites,
            swingType,
            alumThickFrame || false,
          )
        : findMinMaxOutswing(equalPanels || false, sideLites, swingType, isPico);

    setMinWidth(min || 0);
    setMaxWidth(max || 0);
  }, [direction, sideLites, swingType, equalPanels, stilesAndRailsCategory]);

  const swingWidths = useSwingWidth(
    unitSize,
    unitSizeFraction || "",
    direction,
    swingType,
    sideLites,
    swingPanelWidth || "",
    swingPanelFraction || "",
    addToQuote,
    handleError,
    editBtn || false,
    equalPanels || false,
    material,
    isPivot || false,
    addScreen && screenType ? screenType : "",
    alumThickFrame || false,
    isPico,
    exactConfig,
    stilesAndRailsCategory || ""
  );

  const handleWidthChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
    frameOrPanel: string,
  ) => {
    const { value } = event.target;

    if (frameOrPanel === "frame") {
      addToQuote({ unitSize: value }, "unitSize");
    } else {
      let update: UpdateObj = { swingPanelWidth: value };

      const int = Number(value);
      if (int > 90 || (int === 90 && swingPanelFraction)) {
        update["dropseal"] = "";
      }

      addToQuote(update, "swingPanelWidth");
    }
  };

  const handleFractionChange = (
    event: SelectChangeEvent<string>,
    frameOrPanel: string,
  ) => {
    const { value } = event.target;

    if (frameOrPanel === "frame") {
      if (!value || value === "0") {
        addToQuote({ unitSizeFraction: "" });
      } else {
        addToQuote({ unitSizeFraction: value });
      }
    } else {
      if (!value || value === "0") {
        addToQuote({ swingPanelFraction: "" });
      } else {
        let update: UpdateObj = { swingPanelFraction: value };

        if (swingPanelWidth) {
          const int = Number(swingPanelWidth);
          if (int === 90) {
            update["dropseal"] = "";
          }
        }

        addToQuote(update);
      }
    }
  };

  return {
    handleFractionChange,
    handleWidthChange,
    swingWidths,
    minWidth,
    maxWidth,
    overall,
    overallPW,
    sideliteWidthErr: errors.sideliteWidthErr,
    equalPanels,
    unitSize,
    unitSizeFraction,
    swingPanelWidth,
    swingPanelFraction,
  };
}

export default useWidthSelector;
